import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Empty,
  Icon,
  Modal,
  Pagination,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Text,
  useToast,
} from "@app/ui";
import { createPassport, downloadRegistrationsCsv, listPassports, listProducts } from "../api/dpp.js";
import { RequirePermission } from "../components/RequirePermission.js";
import {
  CardGrid,
  CardGridSkeleton,
  EntityCard,
  ListToolbar,
  useViewMode,
  ViewModeSwitch,
} from "../components/ListView.js";
import { PassportQrCell } from "../components/PassportQr.js";
import { ErrorAlert } from "../errors/index.js";
import { useT, useFormatDateTime } from "../i18n.js";
import { NoOrgState } from "../orgs/NoOrgState.js";
import { useOrgs } from "../orgs/OrgContext.js";
import type { Passport, PassportStatus, Product } from "../types.js";

const PAGE_SIZE = 20;

const STATUS_COLOR: Record<PassportStatus, "default" | "success" | "warning"> = {
  draft: "warning",
  published: "success",
  archived: "default",
};

export function PassportsPage() {
  const t = useT();
  const fmtDate = useFormatDateTime();
  const toast = useToast();
  const navigate = useNavigate();
  const { org, loading: orgsLoading } = useOrgs();

  const [items, setItems] = useState<Passport[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [productFilter, setProductFilter] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [view, setView] = useViewMode("passports");

  // Create modal
  const [open, setOpen] = useState(false);
  const [productId, setProductId] = useState("");
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState<unknown>(null);
  const [exporting, setExporting] = useState(false);

  // Product names for table cells + the filter/create selects (first 100 products;
  // the select is searchable client-side).
  useEffect(() => {
    if (!org) return;
    listProducts(org.id, { limit: 100 })
      .then((res) => setProducts(res.items))
      .catch(() => setProducts([]));
  }, [org?.id]);

  useEffect(() => {
    if (!org) return;
    setError(null);
    setLoading(true);
    listPassports(org.id, {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      ...(productFilter ? { productId: productFilter } : {}),
    })
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [org?.id, page, productFilter]);

  // Passports carry only a productId; the product list above supplies the human
  // name/SKU. An id prefix is the fallback when the product is past the first 100.
  const productOf = useMemo(() => {
    const map = new Map(products.map((p) => [p.id, p]));
    return (id: string) => map.get(id);
  }, [products]);
  const productName = (id: string) => {
    const p = productOf(id);
    return p ? `${p.name} (${p.sku})` : id.slice(0, 8);
  };

  if (orgsLoading) return <Spin />;
  if (!org) return <NoOrgState />;

  const productOptions = products.map((p) => ({
    label: `${p.name} (${p.sku})`,
    value: p.id,
    searchText: `${p.name} ${p.sku}`,
  }));

  /** Customs-broker spreadsheet: UPI/URI + CN/GTİP for every registered passport. */
  const exportCustomsCsv = async () => {
    setExporting(true);
    setError(null);
    try {
      const blob = await downloadRegistrationsCsv(org.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dpp-registrations-${org.slug}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e);
    } finally {
      setExporting(false);
    }
  };

  const submitCreate = async () => {
    if (!productId) return;
    setCreateError(null);
    setSaving(true);
    try {
      const created = await createPassport(org.id, { productId });
      toast.success(t("passports.created"));
      setOpen(false);
      navigate(`/passports/${created.id}`);
    } catch (e) {
      setCreateError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title={t("passports.title")}
      extra={
        <Space size="sm">
          <Button
            size="sm"
            variant="subtle"
            icon={<Icon name="download" size={14} />}
            loading={exporting}
            onClick={() => void exportCustomsCsv()}
          >
            {t("passports.exportCustomsCsv")}
          </Button>
          <RequirePermission permission="passport:write">
            <Button
              variant="primary"
              size="sm"
              icon={<Icon name="plus" size={14} />}
              onClick={() => {
                setProductId("");
                setCreateError(null);
                setOpen(true);
              }}
            >
              {t("passports.new")}
            </Button>
          </RequirePermission>
        </Space>
      }
    >
      {error != null && <ErrorAlert error={error} />}

      <ListToolbar
        filters={
          <div style={{ width: 320, maxWidth: "100%" }}>
            <Select
              showSearch
              aria-label={t("passports.product")}
              options={[{ label: t("passports.filterAll"), value: "" }, ...productOptions]}
              value={productFilter}
              onChange={(v) => {
                setProductFilter(v);
                setPage(1);
              }}
            />
          </div>
        }
        right={
          <>
            <Text small secondary aria-live="polite">
              {t("common.totalCount", { count: total })}
            </Text>
            <ViewModeSwitch value={view} onChange={setView} />
          </>
        }
      />

      {!loading && items.length === 0 ? (
        <Empty description={productFilter ? t("common.noSearchResults") : t("passports.empty")}>
          {productFilter ? (
            <Button
              size="sm"
              variant="subtle"
              onClick={() => {
                setProductFilter("");
                setPage(1);
              }}
            >
              {t("common.clearFilters")}
            </Button>
          ) : (
            <RequirePermission permission="passport:write">
              <Button
                variant="primary"
                size="sm"
                icon={<Icon name="plus" size={14} />}
                onClick={() => {
                  setProductId("");
                  setCreateError(null);
                  setOpen(true);
                }}
              >
                {t("passports.new")}
              </Button>
            </RequirePermission>
          )}
        </Empty>
      ) : view === "list" ? (
        <Table
          rowKey={(p) => p.id}
          loading={loading}
          empty={t("passports.empty")}
          onRowClick={(p) => navigate(`/passports/${p.id}`)}
          columns={[
            {
              key: "qr",
              title: t("passports.qr"),
              width: 64,
              render: (p) => <PassportQrCell passport={p} orgId={org.id} label={productName(p.productId)} />,
            },
            {
              key: "product",
              title: t("passports.product"),
              render: (p) => {
                const product = productOf(p.productId);
                return (
                  <div className="min-w-0">
                    <div className="truncate font-medium text-foreground">
                      {product?.name ?? p.productId.slice(0, 8)}
                    </div>
                    {product && (
                      <div className="truncate font-mono text-xs text-muted-foreground">{product.sku}</div>
                    )}
                  </div>
                );
              },
            },
            {
              key: "version",
              title: t("passports.version"),
              width: 110,
              render: (p) =>
                p.currentVersionNo === null ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <span className="tabular-nums">v{p.currentVersionNo}</span>
                ),
            },
            {
              key: "status",
              title: t("common.status"),
              width: 120,
              render: (p) => <Tag color={STATUS_COLOR[p.status]}>{t(`passportStatus.${p.status}`)}</Tag>,
            },
            {
              key: "updatedAt",
              title: t("common.updatedAt"),
              width: 170,
              hideOnMobile: true,
              render: (p) => <span className="text-muted-foreground">{fmtDate(p.updatedAt)}</span>,
            },
          ]}
          data={items}
        />
      ) : loading ? (
        <CardGridSkeleton />
      ) : (
        <CardGrid>
          {items.map((p) => {
            const product = productOf(p.productId);
            return (
              <EntityCard
                key={p.id}
                onOpen={() => navigate(`/passports/${p.id}`)}
                openLabel={productName(p.productId)}
                title={product?.name ?? p.productId.slice(0, 8)}
                code={product?.sku}
                status={<Tag color={STATUS_COLOR[p.status]}>{t(`passportStatus.${p.status}`)}</Tag>}
                meta={[
                  {
                    key: "version",
                    label: t("passports.version"),
                    value: p.currentVersionNo === null ? "—" : `v${p.currentVersionNo}`,
                  },
                  { key: "createdAt", label: t("common.createdAt"), value: fmtDate(p.createdAt) },
                ]}
                footer={`${t("common.updatedAt")}: ${fmtDate(p.updatedAt)}`}
              />
            );
          })}
        </CardGrid>
      )}

      {total > PAGE_SIZE && (
        <div style={{ marginTop: "var(--az-sp-4)" }}>
          <Pagination total={total} pageSize={PAGE_SIZE} current={page} onChange={setPage} />
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t("passports.createTitle")}
        okText={t("common.create")}
        cancelText={t("common.cancel")}
        onOk={() => void submitCreate()}
        confirmLoading={saving}
      >
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          {createError != null && <ErrorAlert error={createError} onRetry={() => void submitCreate()} />}
          <Select
            label={t("passports.product")}
            showSearch
            placeholder={t("passports.selectProduct")}
            options={productOptions}
            value={productId}
            onChange={setProductId}
          />
        </Space>
      </Modal>
    </Card>
  );
}
